import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { conversationStateValues } from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';

/**
 * Depending on the current state of the conversation and it's transfer status,
 * the action taken may either end the conversation for the citizen and the TC
 * or the current user will just leave the conversation and the other party can
 * still chat with each other. This method indicates which of those two actions
 * will be taken for the current user if they click the chat exit button.
 * @param conversation The conversation the action is sought for.
 * @param currentUserAuthorityId The authority ID for the current user.
 */
export function getChatExitActionForCurrentUser(
  conversation: conversationStateValues | undefined,
  currentUserAuthorityId: string | undefined,
): ChatExitAction {
  // if there is no conversation or the transfer has not been accepted, then the
  // user leaving the conversation will end it for everyone.
  if (conversation?.transfer?.status !== 'accepted') {
    return 'end';
  }
  if (
    !conversation.transfer.initiatingAuthorityAccountId ||
    !conversation.transfer.receivingAuthorityAccountId
  ) {
    // if there is missing transfer authority data, default to ending the conversation to be safe
    // eslint-disable-next-line no-console -- log for sentry detection of missing data
    console.error(
      'Conversation transfer is accepted but is missing initiating or receiving authority account IDs',
      conversation.transfer,
    );
    return 'end';
  }

  if (
    conversation.transfer.initiatingAuthorityAccountId ===
    currentUserAuthorityId
  ) {
    // if the current user is part of the initiating authority, then they can safely leave without ending the conversation for the receiving authority
    return 'leave';
  }

  // if the current user is part of the receiving authority (which should be the only other scenario at this point), when they leave, the call ends.
  return 'end';
}

export const getChatButtonText = ({
  conversation,
  isConversationOngoing = false,
  currentUserAuthorityId,
}: {
  conversation: conversationStateValues | undefined;
  isConversationOngoing: boolean;
  currentUserAuthorityId: string | undefined;
}) => {
  if (isConversationOngoing) {
    const exitAction = getChatExitActionForCurrentUser(
      conversation,
      currentUserAuthorityId,
    );
    if (exitAction === 'leave') {
      return 'Leave Chat';
    }
    return 'End Chat';
  }

  switch (conversation?.source) {
    case SOURCE_TCAPI:
      return 'Accept Chat';
    default:
      return 'Start Chat';
  }
};

type ChatExitAction = 'leave' | 'end';
